import {
  PRIMARY_STATUS,
  applySelection,
  revealedGroups,
  type StatusCategory,
} from '@toothsomatics/tooth-model';

/**
 * What a client has recorded about one position.
 *
 * Selections are held per category rather than in one flat list, because the exclusive rules in
 * `applySelection` are per-category — "Not sure" about a filling material says nothing about
 * whether the position is missing.
 */
export interface PositionEntry {
  readonly selections: Partial<Record<StatusCategory, readonly string[]>>;
  readonly note: string;
}

export type ChartEntries = Readonly<Record<number, PositionEntry>>;

/**
 * An image chosen but not yet uploaded.
 *
 * Held apart from `PositionEntry` because its lifecycle is different: chart selections go to the
 * database as rows, an image goes to private storage through a presigned URL and leaves only a
 * `storage_key` behind. Keeping them separate means neither has to know about the other.
 *
 * `previewUrl` comes from `URL.createObjectURL` and must be revoked when the draft is discarded, or
 * the tab holds the whole file in memory for as long as it is open.
 */
export interface ImageDraft {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly contentType: string;
  readonly previewUrl: string;
  readonly file: File;
}

export type ImageDrafts = Readonly<Record<number, readonly ImageDraft[]>>;

export const EMPTY_ENTRY: PositionEntry = { selections: {}, note: '' };

/**
 * Whether a position has anything recorded at all. Drives the chart's "touched" styling, and
 * eventually the readiness check — which the API computes, not the browser.
 */
export function hasContent(entry: PositionEntry | undefined): boolean {
  if (!entry) return false;
  if (entry.note.trim().length > 0) return true;
  return Object.values(entry.selections).some((values) => (values?.length ?? 0) > 0);
}

/**
 * Tick or untick one option, and return the whole entry as it should now be.
 *
 * A pure function, deliberately, and out of the component: this rule has broken twice while it
 * lived inline in an event handler, both times invisibly — a checkbox that would not come on, with
 * nothing in the console to say why. It is small enough to hold in the head and worth a test.
 *
 * Two rules, and only two:
 *
 *   1. The exclusive logic comes from `@toothsomatics/tooth-model` — "Not sure" clears the rest of
 *      its own category, and choosing anything else clears "Not sure". The API validates with the
 *      same function, so the browser cannot disagree with the server about what was selected.
 *
 *   2. Detail answers are cleared **only when the primary status changes**. Untick "Filling" and
 *      the material and surface answers beneath it stop applying, so they go. Ticking an option
 *      *inside* a detail section never clears anything: every section can be opened by hand, and
 *      pruning on that path deleted the tick in the same breath as making it.
 */
export function toggleOption(
  entry: PositionEntry,
  category: StatusCategory,
  value: string,
  selected: boolean,
): PositionEntry {
  const next = applySelection(category, entry.selections[category] ?? [], value, selected);
  const selections: PositionEntry['selections'] = { ...entry.selections, [category]: next };

  if (category === PRIMARY_STATUS.category) {
    const stillRevealed = new Set(revealedGroups(next).map((group) => group.category));

    for (const key of Object.keys(selections) as StatusCategory[]) {
      if (key !== PRIMARY_STATUS.category && !stillRevealed.has(key)) delete selections[key];
    }
  }

  return { ...entry, selections };
}
